/* ==========================================================================
   citron-reels — silent looping product reels

   Playback is driven by visibility rather than by the `autoplay` attribute.
   `autoplay` makes the browser fetch every video during page load regardless
   of where the section sits, which on a row of four 720p files is several
   megabytes competing with the hero and the first product grid. Here the
   videos ship with preload="none" and no source at all; a reel is hydrated
   only once it is close to the viewport, and played only once it is actually
   on screen.

   Two thresholds, deliberately different:
     LOAD  — 400px before the reel arrives, so the file has a head start and
             the swap from poster to moving image is not visible.
     PLAY  — 25% of the reel on screen, so a video is never decoding for a
             customer who cannot see it.
   ========================================================================== */

(function () {
  var LOAD_MARGIN = '400px 200px';
  var PLAY_RATIO = 0.25;

  // One retry hook for browsers that refuse the first play() call. Registered
  // once for the whole page rather than per video.
  var blocked = [];
  var retryBound = false;

  function bindRetry() {
    if (retryBound) return;
    retryBound = true;

    var retry = function () {
      var pending = blocked.slice();
      blocked.length = 0;
      pending.forEach(function (video) {
        attemptPlay(video);
      });
    };

    // A genuine gesture unlocks playback everywhere; passive + once keeps this
    // free of scroll cost.
    ['pointerdown', 'touchstart', 'keydown'].forEach(function (evt) {
      document.addEventListener(evt, retry, { once: true, passive: true });
    });
  }

  function attemptPlay(video) {
    // Belt and braces: the attribute sets defaultMuted, the property covers
    // any script that flipped it. Autoplay is only ever legal while muted.
    video.muted = true;
    video.defaultMuted = true;

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        // Poster stays visible; queue for the next user gesture.
        if (blocked.indexOf(video) === -1) blocked.push(video);
        bindRetry();
      });
    }
  }

  function hydrate(video) {
    if (video.dataset.ctrLoaded) return;
    video.dataset.ctrLoaded = '1';

    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var url = (mobile && video.dataset.srcMobile) || video.dataset.src;
    if (!url) return;

    var source = document.createElement('source');
    source.src = url;
    source.type = video.dataset.type || 'video/mp4';
    video.appendChild(source);

    // 'auto' only now — the element was preload="none" until this moment.
    video.preload = 'auto';
    video.load();
  }

  class CitronReels extends HTMLElement {
    connectedCallback() {
      this.videos = Array.prototype.slice.call(this.querySelectorAll('[data-ctr-reel]'));
      if (!this.videos.length) return;

      // Without IntersectionObserver, load and play everything rather than
      // show a wall of frozen posters. Rare enough not to optimise for.
      if (!('IntersectionObserver' in window)) {
        this.videos.forEach(function (video) {
          hydrate(video);
          attemptPlay(video);
        });
        return;
      }

      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.loader = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            hydrate(entry.target);
            this.loader.unobserve(entry.target);
          }, this);
        }.bind(this),
        { rootMargin: LOAD_MARGIN }
      );

      // Reduced motion: still load so the poster is replaced by a real first
      // frame, but never start playback on its own.
      this.player = reduce
        ? null
        : new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                var video = entry.target;
                if (entry.isIntersecting) {
                  hydrate(video);
                  attemptPlay(video);
                } else if (!video.paused) {
                  // Pause rather than unload: the buffer survives, so coming
                  // back into view resumes instantly with no refetch.
                  video.pause();
                }
              });
            },
            { threshold: PLAY_RATIO }
          );

      this.videos.forEach(
        function (video) {
          this.loader.observe(video);
          if (this.player) this.player.observe(video);
        }.bind(this)
      );

      // Some browsers drop the muted flag across a bfcache restore, which would
      // leave a reel audible on the way back from a product page.
      window.addEventListener('pageshow', function () {
        this.videos.forEach(function (video) {
          video.muted = true;
        });
      }.bind(this));
    }

    disconnectedCallback() {
      if (this.loader) this.loader.disconnect();
      if (this.player) this.player.disconnect();
    }
  }

  if (!customElements.get('citron-reels')) {
    customElements.define('citron-reels', CitronReels);
  }
})();
